import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LandingPage from './LandingPage';

/**
 * AuthAwareRoot - Smart root component that handles authentication-based routing
 * 
 * This component intelligently routes users based on their authentication state:
 * - Authenticated users → Redirected to /dashboard
 * - Unauthenticated users → Shown LandingPage
 * - Preserves URL parameters during redirect
 * - Respects special flows (signature tokens, login states, etc.)
 */
const AuthAwareRoot = () => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();

  // Show loading spinner during authentication check
  // Uses the same styling as ProtectedRoute for consistency
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Check for special URL parameters that should bypass redirection
  const hasSignatureToken = searchParams.has('token');
  const hasLoginParam = searchParams.has('login');
  const hasEmailVerificationParam = searchParams.has('verify');
  const hasPasswordResetParam = searchParams.has('reset');
  const hasInviteParam = searchParams.has('invite');
  const hasWaitlistParam = searchParams.has('waitlist');

  // Special cases where we should show LandingPage even for authenticated users
  const shouldShowLandingPage = (
    hasSignatureToken ||     // Signature token flows need to be processed on landing page
    hasLoginParam ||         // Login completion flows (e.g., ?login=true)
    hasEmailVerificationParam || // Email verification flows
    hasPasswordResetParam || // Password reset flows  
    hasInviteParam ||        // Workspace invitation flows
    hasWaitlistParam         // Waitlist flows
  );

  // If user is authenticated and no special parameters, redirect to dashboard
  if (user && !shouldShowLandingPage) {
    // Preserve any other query parameters during redirect
    const queryString = searchParams.toString();
    const dashboardUrl = queryString ? `/dashboard?${queryString}` : '/dashboard';
    
    console.log('🚀 AuthAwareRoot: Redirecting authenticated user to dashboard:', dashboardUrl);
    return <Navigate to={dashboardUrl} replace />;
  }

  // Show landing page for:
  // 1. Unauthenticated users
  // 2. Authenticated users with special URL parameters that need landing page processing
  console.log('🏠 AuthAwareRoot: Showing landing page', {
    hasUser: !!user,
    shouldShowLandingPage,
    searchParams: Object.fromEntries(searchParams.entries())
  });
  
  return <LandingPage />;
};

export default AuthAwareRoot;
